import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  CircularProgress,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  HourglassEmpty as HourglassEmptyIcon,
  HelpOutline as HelpOutlineIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ErrorOutline as ErrorOutlineIcon,
  Block as BlockIcon,
} from "@mui/icons-material";
import OffboardDialog from "../OffboardingDialogs/OffboardDialog";
import moment from "moment";
import ClassifyDialog from "./ClassifyDialog";
import AssignManagerAndRole from "../OffboardingDialogs/AssignManagerAndRole";
import { useQuery } from "@apollo/client";
import {
  GET_PEOPLE_CHECKS,
  GET_EMPLOYEE_CHECK_STATUSES,
  GET_PEOPLE_CHECK_EXCEPTIONS,
} from "../../../../../../apis/organization-apis/people-apis/peopleApis";
import { getEvidencesByEmployee } from "../../../../../../apis/settingsApis/processed-evidence/processedApis";
import {
  CheckCategory,
  CheckStatus,
  ExceptionScope,
} from "../../../../../../utils/enums";
import { renderCheckDetails as importedRenderCheckDetails } from "../../../../../../pages/Settings/Frameworks/FrameWorkDialogs/Control-Components/ControlComponents";

import ChecksSection from "./components/ChecksSection";
import EvidenceSection from "./components/EvidenceSection";
import StaffHeader from "./components/StaffHeader";
import StaffInfoCard from "./components/StaffInfoCard";
import EditEmployeeDialog from "../EmployeeCRUD/EditEmployeeDialog";
import EditStartDateDialog from "./components/EditStartDateDialog";
import { formatDate } from "../../../../../../utils/dateUtils";

const StaffDetailsDialog = ({
  open,
  onClose,
  staffMember,
  originalEmployee,
  handleDateDifference,
  refetch,
  setValue,
  isTruto,
  data,
  hasHrmsIntegration = false,
  onStaffMemberUpdate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editDialogState, setEditDialogState] = useState({
    isOpen: false,
    title: "",
  });
  const [openClassifyDailog, setOpenClassifyDialog] = useState(false);
  const [viewMode, setViewMode] = useState("checks");
  const [expandedCheckId, setExpandedCheckId] = useState(null);
  const [currentStaffMember, setCurrentStaffMember] = useState(staffMember);
  const [currentOriginalEmployee, setCurrentOriginalEmployee] = useState(originalEmployee);
  const [editEmployeeDialogOpen, setEditEmployeeDialogOpen] = useState(false);
  const [editStartDateDialogOpen, setEditStartDateDialogOpen] = useState(false);
  const [scopeOverride, setScopeOverride] = useState(null);

  const handleOptimisticScopeChange = React.useCallback((inScope, scopeReason) => {
    if (inScope === null || inScope === undefined) {
      setScopeOverride(null);
      return;
    }

    setScopeOverride(inScope);

    if (!inScope && scopeReason) {
      setCurrentStaffMember((prev) =>
        prev ? { ...prev, in_scope: false, scopeReason } : prev
      );
      setCurrentOriginalEmployee((prev) =>
        prev ? { ...prev, in_scope: false, scope_reason: scopeReason } : prev
      );
    } else if (inScope) {
      setCurrentStaffMember((prev) =>
        prev ? { ...prev, in_scope: true, scopeReason: null } : prev
      );
      setCurrentOriginalEmployee((prev) =>
        prev ? { ...prev, in_scope: true, scope_reason: null } : prev
      );
    }
  }, []);

  const [expandedEvidenceId, setExpandedEvidenceId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 5;

  React.useEffect(() => {
    setCurrentStaffMember(staffMember);
    setCurrentOriginalEmployee(originalEmployee);
    setScopeOverride(null);
  }, [staffMember, originalEmployee]);

  const { data: checksData, loading: checksLoading } = useQuery(
    GET_PEOPLE_CHECKS,
    {
      variables: {
        active_check_category: CheckCategory?.POPULATION_CHECK,
      },
    }
  );

  const { data: evidenceData, loading: evidenceLoading } = useQuery(
    getEvidencesByEmployee,
    {
      variables: {
        employeeName: currentStaffMember?.name,
        employeeEmail: currentStaffMember?.email,
      },
      skip: !currentStaffMember?.name && !currentStaffMember?.email,
    }
  );

  const peopleChecks = useMemo(() => {
    const baseChecks = checksData?.getPeopleChecks || [];
    const employeeStatuses = currentStaffMember?.checkStatuses || [];
    return baseChecks.map((check) => {
      const latestStatus = employeeStatuses.find(
        (status) =>
          String(status?.active_check_id) === String(check?.active_check_id)
      );
      let formattedDate = "";
      if (
        latestStatus?.status != CheckStatus.DISABLED &&
        latestStatus?.updated_at
      ) {
        try {
          formattedDate = formatDate(latestStatus?.updated_at);
        } catch (e) {
          console.error("Failed to parse date:", latestStatus?.updated_at, e);
        }
      }
      return {
        ...check,
        status: latestStatus?.status || CheckStatus.PENDING,
        status_reason: latestStatus?.status_reason,
        // status_updated_at: formattedDate
      };
    });
  }, [checksData, currentStaffMember]);

  const handleRefetch = React.useCallback(async () => {
    try {
      const result = await refetch({ filter: {} });
      if (result?.data?.getEmployees?.data) {
        const allEmployees = result.data.getEmployees.data;
        
        const updatedEmployee = allEmployees.find(
          (emp) => 
            (currentOriginalEmployee?.employee_id && emp.employee_id === currentOriginalEmployee.employee_id) ||
            (currentOriginalEmployee?.id && emp.id === currentOriginalEmployee.id) ||
            (currentOriginalEmployee?.emails?.[0]?.email && emp.emails?.[0]?.email === currentOriginalEmployee.emails[0].email) ||
            (currentStaffMember?.email && emp.emails?.[0]?.email === currentStaffMember.email)
        );
        
        if (updatedEmployee) {
          const role = updatedEmployee.employments?.[0]?.job_title || "-";
          const managerData = updatedEmployee.manager;
          let managerName = "-";
          let managerEmail = "";
          
          if (managerData?.id) {
            const managerEmployee = allEmployees.find(
              (emp) => 
                emp.employee_id === managerData.id || 
                emp.id === managerData.id ||
                String(emp.id) === String(managerData.id) ||
                String(emp.employee_id) === String(managerData.id)
            );
            if (managerEmployee) {
              managerName = managerEmployee.name || "-";
              managerEmail = managerEmployee.emails?.[0]?.email || "";
            }
          }
          
          const isInScope = updatedEmployee.in_scope === true;
          
          const updatedStaffMember = {
            ...currentStaffMember,
            name: updatedEmployee.name || currentStaffMember.name,
            role: role,
            manager: managerName,
            managerEmail: managerEmail,
            joinDate: updatedEmployee.start_date || currentStaffMember.joinDate,
            in_scope: isInScope,
            scopeReason: updatedEmployee.scope_reason || "-",
            accountCategory: updatedEmployee.account_category || null,
            accountCategoryKey: updatedEmployee.account_category?.key || null,
            terminationDate: updatedEmployee.termination_date || null,
            _originalEmployee: updatedEmployee,
          };
          setCurrentStaffMember(updatedStaffMember);
          setCurrentOriginalEmployee(updatedEmployee);
          if (onStaffMemberUpdate) {
            onStaffMemberUpdate(updatedStaffMember);
          }
        } 
      }
    } catch (error) {
      console.error("Error refetching employee data:", error);
    }
  }, [refetch, currentOriginalEmployee, currentStaffMember, onStaffMemberUpdate]);

  const checkToEvidences = useMemo(() => {
    const map = {};
    if (peopleChecks && evidenceData?.getEvidencesByEmployee) {
      peopleChecks.forEach((check) => {
        map[String(check?.active_check_id)] = [];
      });

      evidenceData?.getEvidencesByEmployee.forEach((evidence) => {
        const checkId = String(evidence?.check_id);
        if (map[checkId]) {
          map[checkId].push({
            fileId: evidence?.id,
            maskName:
              evidence?.mask_name ||
              (evidence?.path
                ? evidence?.path.split("/").pop()
                : `Evidence ${evidence?.id}`),
            path: evidence?.path,
            isFileValid:
              evidence?.is_file_valid === undefined
                ? true
                : evidence?.is_file_valid,
          });
        }
      });
    }
    return map;
  }, [peopleChecks, evidenceData]);

  const evidenceToChecks = useMemo(() => {
    const evidenceMap = new Map();
    if (peopleChecks && Object.keys(checkToEvidences).length > 0) {
      peopleChecks.forEach((check) => {
        const evidences = checkToEvidences[check?.active_check_id];
        if (evidences) {
          evidences.forEach((evidence) => {
            const evidenceKey = evidence?.path || evidence?.fileId;
            if (!evidenceMap.has(evidenceKey)) {
              evidenceMap.set(evidenceKey, {
                ...evidence,
                associatedChecks: [],
              });
            }
            const existingEvidence = evidenceMap.get(evidenceKey);
            if (
              !existingEvidence?.associatedChecks.some(
                (c) => c?.active_check_id === check?.active_check_id
              )
            ) {
              existingEvidence?.associatedChecks.push(check);
            }
          });
        }
      });
    }
    return Array.from(evidenceMap.values());
  }, [peopleChecks, checkToEvidences]);

  const checkNumberMap = useMemo(() => {
    const map = {};
    if (peopleChecks) {
      peopleChecks.forEach((check, index) => {
        if (check) {
          map[check?.active_check_id] = index + 1;
        }
      });
    }
    return map;
  }, [peopleChecks]);

  const getCheckStatusFromControl = (checkId) => {
    const check = peopleChecks.find(
      (c) => c && String(c?.active_check_id) === String(checkId)
    );
    return check ? check?.status : "fail";
  };

  const paginatedEvidences = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return evidenceToChecks.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [evidenceToChecks, currentPage]);

  const totalPages = Math.ceil(evidenceToChecks.length / ITEMS_PER_PAGE);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const toggleExpandEvidence = (fileId) => {
    setExpandedEvidenceId(expandedEvidenceId === fileId ? null : fileId);
  };

  const toggleExpandCheck = (checkId) => {
    setExpandedCheckId(expandedCheckId === checkId ? null : checkId);
    if (expandedCheckId === checkId) {
      setExpandedEvidenceId(null);
    }
  };

  const renderAdaptedCheckDetails = (associatedChecks, validationStatus) => {
    if (!associatedChecks || !validationStatus) return null;

    const mappedChecks = associatedChecks.map((c) => ({
      checkId: c?.active_check_id,
      checkDescription: c?.active_check_description,
    }));

    return importedRenderCheckDetails(
      mappedChecks,
      validationStatus,
      checkNumberMap,
      getCheckStatusFromControl
    );
  };

  const getFileValidationStatus = (evidence) => {
    if (evidence?.isFileValid) {
      return {
        status: "valid",
        message: "File is valid",
        showChecks: true,
        dotColor: "#22c55e",
      };
    }
  };

  const handleDownloadDocument = (path, name) => {
    if (path) {
      const link = document.createElement("a");
      link.href = `${process.env?.REACT_APP_CLOUDFRONT_URL}/${path}`;
      link.download = name;
      link.click();
    }
  };

  const isLoading = checksLoading || evidenceLoading;

  if (!currentStaffMember) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            maxHeight: "95vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "1.25rem",
            background: "white",
            color: "#031B89",
            position: "relative",
            py: 2,
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Staff Details
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: "#031B89",
              backgroundColor: "transparent",
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, backgroundColor: "#f8fafc" }}>
          <StaffHeader
            staffMember={currentStaffMember}
            originalEmployee={currentOriginalEmployee}
            refetch={handleRefetch}
            onEditName={() => setEditEmployeeDialogOpen(true)}
            hasHrmsIntegration={hasHrmsIntegration}
            optimisticInScope={scopeOverride}
            onOptimisticScopeChange={handleOptimisticScopeChange}
          />
          <StaffInfoCard
            staffMember={currentStaffMember}
            originalEmployee={currentOriginalEmployee}
            refetch={handleRefetch}
            hasHrmsIntegration={hasHrmsIntegration}
            onEditStartDate={() => setEditStartDateDialogOpen(true)}
            onScopeChange={handleOptimisticScopeChange}
            onScopeSaveSuccess={() => handleOptimisticScopeChange(null)}
            onScopeSaveError={() => handleOptimisticScopeChange(null)}
          />

          <Box sx={{ p: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, val) => val && setViewMode(val)}
                aria-label="checks or evidences"
                sx={{
                  background: "white",
                  borderRadius: 2,
                  border: "1px solid #e5e7eb",
                  "& .MuiToggleButton-root": {
                    border: "none",
                    borderRadius: 2,
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    color: "#64748b",
                    background: "white",
                    transition: "all 0.3s ease-in-out",
                    "&.Mui-selected": {
                      backgroundColor: "#031B89",
                      color: "white",
                    },
                  },
                }}
              >
                <ToggleButton value="checks">
                  Checks ({peopleChecks.length})
                </ToggleButton>
                <ToggleButton value="evidences">
                  Evidences ({evidenceToChecks.length})
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {isLoading && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={8}
              >
                <CircularProgress size={40} />
                <Typography variant="body1" sx={{ ml: 2, color: "#64748b" }}>
                  Loading data...
                </Typography>
              </Box>
            )}

            {!isLoading && (
              <div>
                <Collapse
                  in={viewMode === "checks"}
                  timeout={400}
                  mountOnEnter
                  unmountOnExit
                >
                  <ChecksSection
                    checks={peopleChecks}
                    expandedCheckId={expandedCheckId}
                    onToggleExpand={toggleExpandCheck}
                    checkToEvidences={checkToEvidences}
                    handleDownloadDocument={handleDownloadDocument}
                  />
                </Collapse>

                <Collapse
                  in={viewMode === "evidences"}
                  timeout={400}
                  mountOnEnter
                  unmountOnExit
                >
                  <EvidenceSection
                    totalItems={evidenceToChecks.length}
                    paginatedData={paginatedEvidences}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    handlePageChange={handlePageChange}
                    expandedEvidenceId={expandedEvidenceId}
                    toggleExpand={toggleExpandEvidence}
                    handleDownloadDocument={handleDownloadDocument}
                    getFileValidationStatus={getFileValidationStatus}
                    renderCheckDetails={renderAdaptedCheckDetails}
                    ITEMS_PER_PAGE={ITEMS_PER_PAGE}
                    staffMember={currentStaffMember}
                  />
                </Collapse>
              </div>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <OffboardDialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        staffMember={currentStaffMember}
        refetch={handleRefetch}
        closeDetailDialog={onClose}
      />
      <AssignManagerAndRole
        open={editDialogState.isOpen}
        onClose={() => setEditDialogState({ isOpen: false, title: "" })}
        title={editDialogState.title}
        isTruto={isTruto}
        staffMember={currentStaffMember}
        data={data}
        refetch={handleRefetch}
        closeDetailDialog={onClose}
      />
      {/* <ClassifyDialog
        open={openClassifyDailog}
        handleClose={() => setOpenClassifyDialog(false)}
        user={staffMember}
        refetch={refetch}
        setValue={setValue}
        closeDetailDialog={onClose}
      /> */}
      <EditEmployeeDialog
        open={editEmployeeDialogOpen}
        onClose={() => setEditEmployeeDialogOpen(false)}
        refetch={handleRefetch}
        employee={currentOriginalEmployee}
        hasHrmsIntegration={hasHrmsIntegration}
      />
      <EditStartDateDialog
        open={editStartDateDialogOpen}
        onClose={() => setEditStartDateDialogOpen(false)}
        employee={currentOriginalEmployee}
        currentStartDate={currentStaffMember?.joinDate || currentOriginalEmployee?.start_date}
        refetch={handleRefetch}
        hasHrmsIntegration={hasHrmsIntegration}
      />
    </>
  );
};

export default StaffDetailsDialog;
